/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ErrorWithInfo, useMutate } from '@/net/commons';

const urlSchema = z.object({
    webhookUrl: z.string().url({ message: 'Invalid URL format' }),
});
type AddWebhookDialogProps = {
    type: 'slackWebhookUrl' | 'discordWebhookUrl';
    mode: 'add' | 'edit';
    onUpdate?: () => void;
};

export default function AddWebhookDialog({ type, mode, onUpdate }: AddWebhookDialogProps) {
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const form = useForm<z.infer<typeof urlSchema>>({
        defaultValues: {
            webhookUrl: '',
        },
        resolver: zodResolver(urlSchema),
    });
    const { trigger: triggerUpdate } = useMutate(`/bff/organisation/webhook-url`, {
        revalidate: true,
        method: 'PATCH',
    });
    const onSubmit = async (values: z.infer<typeof urlSchema>) => {
        try {
            setIsLoading(true);
            await triggerUpdate({ [type]: values.webhookUrl });
            setOpen(false);
            onUpdate && onUpdate();
        } catch (err) {
            setIsLoading(false);
            const message = (err as ErrorWithInfo)?.info?.error ?? 'There was a problem with your request';
            form.setError('webhookUrl', { message });
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                form.reset();
                setOpen(o);
                setIsLoading(false);
            }}
        >
            <DialogTrigger asChild>
                <Button className="h-min w-min whitespace-nowrap">{mode === 'edit' ? 'Update' : 'Add'}</Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Add Webhook Url</DialogTitle>
                    <DialogDescription>
                        Set a webhook URL to trigger notifications. Notifications can be turned on and off in suite
                        settings.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-10">
                        <FormField
                            control={form.control}
                            name="webhookUrl"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex flex-row gap-2 items-center">Webhook URL</FormLabel>
                                    <FormControl>
                                        <Input placeholder="" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className="flex flex-row justify-end">
                            <Button type="submit" disabled={isLoading}>
                                {isLoading ? 'Saving...' : 'Save'}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
